import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

export const apiClient = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
    headers: {
        'Content-Type': 'application/json',
    },
});

let isRefreshing = false;
let failedQueue: Array<{
    resolve: (value?: unknown) => void;
    reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

apiClient.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

apiClient.interceptors.response.use(
    (response) => {
        // Reject business-level errors returned with HTTP 200 but non-200 code in body
        const body = response.data;
        if (body && typeof body.code === 'number' && body.code !== 200) {
            return Promise.reject(new Error(body.message || 'Request failed'));
        }
        return response;
    },
    async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        // If 401 and not already retrying and not the refresh or login endpoint
        if (
            error.response?.status === 401 &&
            !originalRequest._retry &&
            !originalRequest.url?.includes('/auth/refresh') &&
            !originalRequest.url?.includes('/auth/login')
        ) {
            if (isRefreshing) {
                // Queue this request
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                    .then((token) => {
                        if (originalRequest.headers) {
                            originalRequest.headers.Authorization = `Bearer ${token}`;
                        }
                        return apiClient(originalRequest);
                    })
                    .catch((err) => Promise.reject(err));
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    throw new Error('No token available');
                }

                const response = await apiClient.post('/auth/refresh');
                const newToken = response.data.data.token;

                const responseData = response.data.data;
                localStorage.setItem('token', newToken);
                localStorage.setItem('user', JSON.stringify({
                    username: responseData.username,
                    roles: responseData.roles,
                    permissions: responseData.permissions,
                }));

                if (originalRequest.headers) {
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                }

                processQueue(null, newToken);
                return apiClient(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError as Error, null);
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                window.location.href = '/login';
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        // 402 Payment Required — backend's SubscriptionRequiredException. Surface a
        // toast pointing at the upgrade page rather than letting a raw error bubble.
        // The wrapping <SubscriptionGate> hides most routes proactively; this catches
        // the cases where a user fires an API call that hits a tier-gated endpoint
        // (e.g. clicks the Taobao sync button after their tier expired mid-session).
        if (error.response?.status === 402) {
            try {
                // dynamic import + lazy load — keeps the api-client free of UI deps
                // for unit-test friendliness, but still wires a nice toast in the SPA.
                const data = (error.response.data as { data?: { feature?: string; requiredTier?: string } })?.data;
                const message = (error.response.data as { message?: string })?.message
                    || 'This feature requires a higher subscription tier.';
                import('antd').then(({ notification }) => {
                    notification.warning({
                        message: data?.requiredTier ? `Requires ${data.requiredTier} plan` : 'Upgrade required',
                        description: message,
                        btn: undefined,
                        duration: 6,
                        placement: 'topRight',
                    });
                });
            } catch { /* notification is best-effort */ }
            return Promise.reject(error);
        }

        // 403 Forbidden — if no token exists, user is not authenticated (e.g., race condition
        // before backend fix propagates). Redirect to login. Otherwise treat as permission denied.
        if (error.response?.status === 403) {
            const token = localStorage.getItem('token');
            if (!token) {
                window.location.href = '/login';
            }
            return Promise.reject(error);
        }

        // For other 401s (like login failure or refresh failure), just reject
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            if (!window.location.pathname.includes('/login')) {
                window.location.href = '/login';
            }
        }

        return Promise.reject(error);
    }
);
